import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import { i18n } from '../../i18n/i18n';
import { ROOM_TYPES, generateAutoLabel } from '../../constants/room_types';
import { addFamilyMember, updateFamily, markOnboardingComplete, updateUser, createRoom, deleteRoom, updateRoom as apiUpdateRoom,
  getSetupOptions, applySetup, updateFamilyMember, SetupRewardOption, SetupRoomOption, SetupTaskOption, setAdultsNeedApprovalApi } from '../../services/apiService';
import { useLanguage } from '../../context/LanguageContext';
import { ageLabel } from '../admin/adminHelpers';
import { AGE_ORDER, childRoomName, suggestRewards, suggestTasks, SetupChild, SetupRoom } from '../../utils/familySetup';
import { pluralKey } from '../../utils/starGoal';

type MemberDraft = {
  name: string;
  username: string;
  pin: string; // numeric string, 4 digits
  is_admin: boolean;
  member_type: 'adult' | 'child';
  /** Children: age group, used to suggest tasks that suit them (2026-10-15). */
  age_group?: string;
};

type RoomDraft = {
  /** Stable key (rooms can be removed), so tasks can be placed in the right room. */
  key: string;
  /** A child's own room: the key of that child ("child-0", "child-1" …). */
  childKey?: string;
  /** Named automatically ("Magnes værelse"); follows the child's name until the parent renames it. */
  auto?: boolean;
  /** Which standard tasks belong in this room (from Master Admin's standard rooms). */
  task_room?: string | null;
  type_key: string; // from ROOM_TYPES or 'custom'
  original_name: string; // display_name or custom
  name: string; // label shown to user (may include index)
};

type ExistingRoom = {
  id: string;
  name: string;
  original_name: string;
};

const OnboardingWizard: React.FC = () => {
  const navigate = useNavigate();
  const { current_user, current_family, refreshFamilyData } = useAppContext() as any;

  const [step, setStep] = useState<number>(1);
  // Grown-ups' tasks need another grown-up's approval? Off unless ticked (2026-10-19).
  const [adultsNeedApproval, setAdultsNeedApproval] = useState(false);
  const [family_name, setFamilyName] = useState<string>('');
  const [adults_count, setAdultsCount] = useState<number>(1);
  const [children_count, setChildrenCount] = useState<number>(0);
  const [members, setMembers] = useState<MemberDraft[]>([]);
  const [room_drafts, setRoomDrafts] = useState<RoomDraft[]>([]);
  const [custom_room_input, setCustomRoomInput] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [show_errors, setShowErrors] = useState<boolean>(false);
  const { current_language } = useLanguage();
  // Starter tasks and rewards (steps 3–4): what the catalog offers, and what the parent ticked.
  const [options, setOptions] = useState<{ tasks: SetupTaskOption[]; rewards: SetupRewardOption[]; rooms?: SetupRoomOption[] } | null>(null);
  const [optionsError, setOptionsError] = useState(false);
  const [picked, setPicked] = useState<Record<string, boolean>>({});
  const [result, setResult] = useState<{ tasks: number; rewards: number } | null>(null);
  const newKey = () => Math.random().toString(36).slice(2, 10);

  // Initialize from current family — once: a background refresh mid-setup must not wipe the
  // rooms the parent (or the children's own rooms) added.
  const initialized = useRef(false);
  useEffect(() => {
    if (current_family && !initialized.current) {
      initialized.current = true;
      setFamilyName(current_family.name || '');
      // Preload existing rooms as drafts
      const preRooms: RoomDraft[] = (current_family.rooms || []).map((r: any) => ({
        key: `existing-${r.id}`,
        type_key: 'existing',
        original_name: r.original_name,
        name: r.name,
      }));
      setRoomDrafts(preRooms);
    }
  }, [current_family]);

  // Generate member rows from counts, but preserve edits
  useEffect(() => {
    const desired: MemberDraft[] = [];
    for (let i = 0; i < adults_count; i++) {
      const existing = members[i];
      desired.push({
        name: existing?.name || '',
        username: existing?.username || (i === 0 ? (current_user?.username || '') : ''),
        pin: existing?.pin || '',
        is_admin: true,
        member_type: 'adult',
      });
    }
    for (let j = 0; j < children_count; j++) {
      const existing = members[adults_count + j];
      desired.push({
        name: existing?.name || '',
        username: existing?.username || '',
        pin: existing?.pin || '',
        is_admin: false,
        member_type: 'child',
        age_group: existing?.age_group || '',
      });
    }
    setMembers(desired);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adults_count, children_count]);

  // Helpers
  const addRoomByType = (type_key: string, display_name: string) => {
    const same = room_drafts.filter(r => r.original_name === display_name).length + 1;
    const label = generateAutoLabel(display_name, same);
    setRoomDrafts(prev => [...prev, { key: newKey(), type_key, original_name: display_name, name: label }]);
  };

  // A standard room from Master Admin (2026-10-15): shown in the family's language, saved with its English name.
  const addCatalogRoom = (r: SetupRoomOption) => {
    const same = room_drafts.filter(d => d.type_key === r.room_key).length + 1;
    setRoomDrafts(prev => [...prev, { key: newKey(), type_key: r.room_key, original_name: r.english_name, task_room: r.task_room, name: generateAutoLabel(r.name, same) }]);
  };

  const addCustomRoom = () => {
    const dn = custom_room_input.trim();
    if (!dn) return;
    const same = room_drafts.filter(r => r.original_name === dn).length + 1;
    const label = generateAutoLabel(dn, same);
    setRoomDrafts(prev => [...prev, { key: newKey(), type_key: 'custom', original_name: dn, name: label }]);
    setCustomRoomInput('');
  };

  const removeRoomAt = (idx: number) => {
    setRoomDrafts(prev => prev.filter((_, i) => i !== idx));
  };

  // Children and their own rooms (2026-10-15): each child gets "Magnes værelse", so their own tasks go there.
  const children: SetupChild[] = members
    .map((m, i) => ({ m, i }))
    .filter(({ m }) => m.member_type === 'child')
    .map(({ m }, j) => ({ key: `child-${j}`, name: m.name.trim(), age_group: m.age_group || null }));
  const tr = (k: string) => i18n.t(k);
  const syncChildRooms = () => {
    setRoomDrafts(prev => {
      const others = prev.filter(r => !r.childKey || children.some(c => c.key === r.childKey));
      for (const c of children) {
        const auto = childRoomName(c.name, tr);
        const own = others.find(r => r.childKey === c.key);
        if (!own) others.push({ key: newKey(), childKey: c.key, auto: true, type_key: 'child_bedroom', original_name: 'Child Bedroom', task_room: 'Child Bedroom', name: auto });
        else if (own.auto) own.name = auto;
      }
      return [...others];
    });
  };
  const setupRooms: SetupRoom[] = room_drafts.map(r => ({ key: r.key, name: r.name, original_name: r.original_name, childKey: r.childKey ?? null, task_room: r.task_room }));
  const taskSuggestions = useMemo(() => (options ? suggestTasks(options.tasks, children, setupRooms) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [options, JSON.stringify(children), JSON.stringify(setupRooms)]);
  const rewardSuggestions = useMemo(() => (options ? suggestRewards(options.rewards, children) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [options, JSON.stringify(children)]);
  const isPicked = (id: string, byDefault: boolean) => (id in picked ? picked[id] : byDefault);
  const pickedTasks = taskSuggestions.filter(s => isPicked(s.id, s.checked));
  const pickedRewards = rewardSuggestions.filter(s => isPicked(s.id, s.checked));
  const loadOptions = async () => {
    if (options) return;
    try { setOptions(await getSetupOptions(current_language)); } catch { setOptionsError(true); }
  };
  // Loaded at the start: step 2 already needs the standard rooms.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void loadOptions(); }, []);

  const canProceedFamily = useMemo(() => {
    if (!family_name.trim()) return false;
    if (adults_count < 1) return false;
    // usernames unique and pins 4-digit numeric
    const usernames = new Set<string>();
    for (const m of members) {
      if (!m.name.trim()) return false;
      if (!m.username || !m.username.trim()) return false;
      if (usernames.has(m.username.trim())) return false;
      usernames.add(m.username.trim());
      if (!/^\d{4}$/.test(m.pin || '')) return false;
    }
    return true;
  }, [family_name, adults_count, members]);

  const canProceedRooms = useMemo(() => room_drafts.length >= 1, [room_drafts.length]);

  // Validation helpers for step 1 highlighting
  const usernameCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const m of members) {
      const u = (m.username || '').trim();
      if (!u) continue;
      counts.set(u, (counts.get(u) || 0) + 1);
    }
    return counts;
  }, [members]);

  const onSave = async () => {
    setError('');
    setSaving(true);
    try {
      // Update family name if changed
      if (current_family?.name !== family_name.trim()) {
        await updateFamily(family_name.trim());
      }

      // Update current admin (index 0) if username/pin provided
      if (members[0]) {
        const adminDraft = members[0];
        const updates: any = {};
        if (adminDraft.username && adminDraft.username !== (current_user?.username || '')) {
          updates.username = adminDraft.username;
        }
        if (adminDraft.pin && /^\d{4}$/.test(adminDraft.pin)) {
          updates.pin = adminDraft.pin;
        }
        if (Object.keys(updates).length > 0) {
          await updateUser(updates);
        }
      }

      // Create the remaining members (skip index 0 if represents current admin); remember their ids
      // so each child's own tasks and rewards can be given to them.
      const memberIds: string[] = [current_user?.id];
      for (const m of members.slice(1)) {
        const created = await addFamilyMember(m.name.trim(), m.username.trim(), m.pin.trim(), undefined, m.is_admin, m.member_type,
          m.member_type === 'child' ? (m.age_group || null) : null);
        memberIds.push((created as any)?.id);
      }
      const childIds: Record<string, string> = {};
      members.forEach((m, i) => {
        if (m.member_type !== 'child') return;
        const j = members.slice(0, i).filter(x => x.member_type === 'child').length;
        childIds[`child-${j}`] = memberIds[i];
      });

      // Reconcile rooms: delete those not in drafts, create new ones, rename labels
      const existing: ExistingRoom[] = (current_family?.rooms || []) as ExistingRoom[];
      const wantedLabels = new Set(room_drafts.map(r => r.name));
      // Delete rooms that are not desired
      for (const r of existing) {
        // Keep if label exists in drafts by name match; otherwise delete
        if (!wantedLabels.has(r.name)) {
          await deleteRoom(r.id);
        }
      }
      // Create any draft not present in existing by label
      const existingByName = new Map<string, ExistingRoom>(existing.map((r: ExistingRoom) => [r.name, r]));
      const roomIds: Record<string, string> = {};
      for (const d of room_drafts) {
        const match = existingByName.get(d.name);
        if (!match) {
          const room = await createRoom({ name: d.name, original_name: d.original_name });
          roomIds[d.key] = (room as any)?.id;
        } else {
          if (match.original_name !== d.original_name || match.name !== d.name) {
            await apiUpdateRoom(match.id, { name: d.name, original_name: d.original_name });
          }
          roomIds[d.key] = match.id;
        }
      }

      // Starter tasks and rewards the parent ticked (2026-10-15): copies of the standard ones.
      let made = { tasks: 0, rewards: 0 };
      if (pickedTasks.length || pickedRewards.length) {
        made = await applySetup({
          language_code: current_language,
          tasks: pickedTasks.map(s => ({
            template_id: s.option.template_id,
            assigned_to: s.childKey ? childIds[s.childKey] ?? null : null,
            room_id: s.roomKey ? roomIds[s.roomKey] ?? null : null,
          })),
          rewards: pickedRewards.map(s => ({ group_id: s.option.group_id, target_id: s.childKey ? childIds[s.childKey] ?? null : null })),
        });
      }

      if (adultsNeedApproval) await setAdultsNeedApprovalApi(true);
      await markOnboardingComplete();
      await refreshFamilyData();
      setResult(made);
      setStep(6);
    } catch (e: any) {
      setError(e?.message || 'Failed to complete onboarding');
    } finally {
      setSaving(false);
    }
  };

  const starsOf = (n: number) => i18n.t(pluralKey('common.stars', n)).replace('{{n}}', String(n));
  const childName = (key: string | null) => children.find(c => c.key === key)?.name || '';
  const roomName = (key: string | null) => room_drafts.find(r => r.key === key)?.name || '';
  const tick = (id: string, on: boolean) => setPicked(prev => ({ ...prev, [id]: on }));
  const skipLink = (next: number) => (
    <button type="button" className="text-sm underline text-gray-600 dark:text-gray-300" onClick={() => {
      // Skipping = nothing from this step is created.
      setPicked(prev => {
        const off = { ...prev };
        for (const sgg of next === 4 ? taskSuggestions : rewardSuggestions) off[sgg.id] = false;
        return off;
      });
      setStep(next);
    }}>{i18n.t('onboarding.skip_step')}</button>
  );
  const row = (id: string, checked: boolean, title: string, sub: string) => (
    <label key={id} className="flex items-start gap-3 py-2 cursor-pointer">
      <input type="checkbox" className="w-5 h-5 mt-0.5" checked={isPicked(id, checked)} onChange={e => tick(id, e.target.checked)} />
      <span><span className="font-medium">{title}</span> <span className="text-sm text-gray-600 dark:text-gray-300">· {sub}</span></span>
    </label>
  );

  function renderStarterTasks() {
    const perChild = children.map(c => ({ c, items: taskSuggestions.filter(s => s.childKey === c.key) })).filter(g => g.items.length);
    const shared = taskSuggestions.filter(s => !s.childKey);
    return (
      <div className="space-y-4">
        <div>
          <h2 className="text-xl font-semibold mb-1">{i18n.t('onboarding.step_tasks_title')}</h2>
          <p className="text-sm text-gray-700 dark:text-gray-300">{i18n.t('onboarding.step_tasks_help')}</p>
        </div>
        {!options && !optionsError && <p>{i18n.t('common.loading')}</p>}
        {optionsError && <p className="text-red-600">{i18n.t('onboarding.starters_unavailable')}</p>}
        {perChild.map(({ c, items }) => (
          <div key={c.key} className="rounded-lg border p-3">
            <h3 className="font-semibold">{i18n.t('onboarding.tasks_for_child').replace('{{name}}', c.name)}</h3>
            {items.map(sg => row(sg.id, sg.checked, sg.option.title, `${starsOf(sg.option.points)} · ${roomName(sg.roomKey)}`))}
          </div>
        ))}
        {shared.length > 0 && (
          <div className="rounded-lg border p-3">
            <h3 className="font-semibold">{i18n.t('onboarding.tasks_shared')}</h3>
            {shared.map(sg => row(sg.id, sg.checked, sg.option.title,
              `${starsOf(sg.option.points)} · ${sg.roomKey ? roomName(sg.roomKey) : i18n.t('onboarding.whole_home')}`))}
          </div>
        )}
        <p className="text-sm text-gray-600 dark:text-gray-300">{i18n.t('onboarding.hint_tasks')}</p>
        <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
          <button className="px-4 py-2 rounded text-btn-text-cancel bg-btn-cancel" onClick={() => setStep(2)}>{i18n.t('onboarding.back')}</button>
          <div className="flex items-center gap-4">
            {skipLink(4)}
            <button className="px-4 py-2 rounded text-btn-text-primary bg-btn-success" onClick={() => setStep(4)}>{i18n.t('onboarding.next')}</button>
          </div>
        </div>
      </div>
    );
  }

  function renderStarterRewards() {
    const perChild = children.map(c => ({ c, items: rewardSuggestions.filter(s => s.childKey === c.key) })).filter(g => g.items.length);
    const family = rewardSuggestions.filter(s => !s.childKey);
    return (
      <div className="space-y-4">
        <div>
          <h2 className="text-xl font-semibold mb-1">{i18n.t('onboarding.step_rewards_title')}</h2>
          <p className="text-sm text-gray-700 dark:text-gray-300">{i18n.t('onboarding.step_rewards_help')}</p>
        </div>
        {perChild.map(({ c, items }) => (
          <div key={c.key} className="rounded-lg border p-3">
            <h3 className="font-semibold">{i18n.t('onboarding.rewards_for_child').replace('{{name}}', c.name)}</h3>
            {items.map(sg => row(sg.id, sg.checked, sg.option.title, starsOf(sg.option.points_required)))}
          </div>
        ))}
        {family.length > 0 && (
          <div className="rounded-lg border p-3">
            <h3 className="font-semibold">{i18n.t('onboarding.rewards_family')}</h3>
            {family.map(sg => row(sg.id, sg.checked, sg.option.title, starsOf(sg.option.points_required)))}
          </div>
        )}
        <p className="text-sm text-gray-600 dark:text-gray-300">{i18n.t('onboarding.hint_rewards')}</p>
        <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
          <button className="px-4 py-2 rounded text-btn-text-cancel bg-btn-cancel" onClick={() => setStep(3)}>{i18n.t('onboarding.back')}</button>
          <div className="flex items-center gap-4">
            {skipLink(5)}
            <button className="px-4 py-2 rounded text-btn-text-primary bg-btn-success" onClick={() => setStep(5)}>{i18n.t('onboarding.next')}</button>
          </div>
        </div>
      </div>
    );
  }

  // Render
  return (
    <div className="max-w-4xl mx-auto p-6 pb-32">
      <h1 className="text-2xl font-semibold mb-4">{i18n.t('onboarding.title')}</h1>

      {step <= 5 && (
        <>
          {/* Stepper */}
          <div className="flex items-center gap-2 mb-4">
            {[1,2,3,4,5].map(n => (
              <div key={n} className={`px-3 py-1 rounded-full text-sm ${step===n ? 'bg-btn-success text-btn-text-primary' : 'bg-gray-200 text-gray-700'}`}>{n}</div>
            ))}
          </div>
          {/* Jens 2026-10-15: say plainly what the information is for. */}
          <p className="mb-6 rounded-lg bg-green-50 dark:bg-green-950/40 px-4 py-3 text-sm text-gray-800 dark:text-gray-100">
            <span aria-hidden>🔒 </span>{i18n.t('onboarding.privacy_note')}
          </p>
        </>
      )}

      {step === 1 && (
        <div className="space-y-4">
          <p className="text-sm text-gray-700 whitespace-pre-line">{i18n.t('onboarding.step_members_help')}</p>
          <div>
            <label htmlFor="setup-family-name" className="block text-sm mb-1">{i18n.t('onboarding.family_name')}</label>
            <input
              id="setup-family-name"
              value={family_name}
              onChange={e => { setFamilyName(e.target.value); if (show_errors) setShowErrors(false); }}
              aria-invalid={show_errors && !family_name.trim() ? 'true' : undefined}
              className={`w-full border rounded px-3 py-2 ${show_errors && !family_name.trim() ? 'border-red-500 ring-1 ring-red-500' : ''}`}
            />
          </div>
          <div className="flex gap-4">
            <div>
              <label className="block text-sm mb-1">{i18n.t('onboarding.adults')}</label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="px-2 py-1 rounded text-btn-text-secondary bg-btn-secondary"
                  onClick={() => setAdultsCount(v => Math.max(1, v - 1))}
                >−</button>
                <input
                  type="number"
                  min={1}
                  value={adults_count}
                  onChange={e => setAdultsCount(Math.max(1, Number(e.target.value)||1))}
                  className={`w-24 border rounded px-3 py-2 ${show_errors && adults_count < 1 ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                />
                <button
                  type="button"
                  className="px-2 py-1 rounded text-btn-text-secondary bg-btn-secondary"
                  onClick={() => setAdultsCount(v => v + 1)}
                >+</button>
              </div>
            </div>
            <div>
              <label className="block text-sm mb-1">{i18n.t('onboarding.children')}</label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="px-2 py-1 rounded text-btn-text-secondary bg-btn-secondary"
                  onClick={() => setChildrenCount(v => Math.max(0, v - 1))}
                >−</button>
                <input
                  type="number"
                  min={0}
                  value={children_count}
                  onChange={e => setChildrenCount(Math.max(0, Number(e.target.value)||0))}
                  className="w-24 border rounded px-3 py-2"
                />
                <button
                  type="button"
                  className="px-2 py-1 rounded text-btn-text-secondary bg-btn-secondary"
                  onClick={() => setChildrenCount(v => v + 1)}
                >+</button>
              </div>
            </div>
          </div>
          <div className="space-y-3">
            {members.map((m, idx) => (
              <div key={idx} className="grid md:grid-cols-4 gap-3 border rounded p-3">
                {(() => {
                  const isAdult = idx < adults_count;
                  const order = isAdult ? (idx + 1) : (idx - adults_count + 1);
                  const nameInvalid = !m.name.trim();
                  const usernameTrim = (m.username || '').trim();
                  const usernameInvalid = usernameTrim.length === 0 || ((usernameCounts.get(usernameTrim) || 0) > 1);
                  const pinInvalid = !/^\d{4}$/.test(m.pin || '');
                  const namePlaceholder = `${i18n.t(isAdult ? 'onboarding.adult_label' : 'onboarding.child_label')} ${order}`;
                  return (
                    <>
                      <input
                        value={m.name}
                        onChange={e => setMembers(prev => prev.map((pm, i) => {
                          if (i !== idx) return pm;
                          const newName = e.target.value;
                          const prevFirst = (pm.name || '').trim().split(/\s+/)[0] || '';
                          const newFirst = (newName || '').trim().split(/\s+/)[0] || '';
                          const shouldAutofill = !pm.username || pm.username === prevFirst;
                          return { ...pm, name: newName, username: shouldAutofill ? newFirst : pm.username };
                        }))}
                        placeholder={namePlaceholder}
                        aria-invalid={show_errors && nameInvalid ? 'true' : undefined}
                        className={`border rounded px-3 py-2 ${show_errors && nameInvalid ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                      />
                      <input
                        value={m.username}
                        onChange={e => setMembers(prev => prev.map((pm, i) => i===idx ? { ...pm, username: e.target.value } : pm))}
                        placeholder={i18n.t('onboarding.username')}
                        aria-invalid={show_errors && usernameInvalid ? 'true' : undefined}
                        className={`border rounded px-3 py-2 ${show_errors && usernameInvalid ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                      />
                      <input
                        value={m.pin}
                        onChange={e => { const v = e.target.value.replace(/[^0-9]/g,'').slice(0,4); setMembers(prev => prev.map((pm, i) => i===idx ? { ...pm, pin: v } : pm)); }}
                        placeholder={i18n.t('onboarding.pin_placeholder')}
                        aria-invalid={show_errors && pinInvalid ? 'true' : undefined}
                        className={`border rounded px-3 py-2 ${show_errors && pinInvalid ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                      />
                    </>
                  );
                })()}
                {m.member_type === 'child' ? (
                  // A child's age: the setup suggests tasks that suit it (2026-10-15).
                  <select value={m.age_group || ''} aria-label={i18n.t('onboarding.child_age')}
                    onChange={e => setMembers(prev => prev.map((pm, i) => i===idx ? { ...pm, age_group: e.target.value } : pm))}
                    className="border rounded px-3 py-2">
                    <option value="">{i18n.t('onboarding.child_age')}</option>
                    {AGE_ORDER.filter(a => a !== 'adults').map(a => <option key={a} value={a}>{ageLabel(a as any)}</option>)}
                  </select>
                ) : (
                  <div className="flex items-center text-sm"><span className="px-2 py-1 rounded bg-gray-100">{m.is_admin ? i18n.t('onboarding.role_admin') : i18n.t('onboarding.role_member')}</span></div>
                )}
              </div>
            ))}
          </div>
          {show_errors && !canProceedFamily && (
            <div className="text-red-600 text-sm">{i18n.t('onboarding.validation_incomplete')}</div>
          )}
          <p className="text-sm text-gray-600 dark:text-gray-300">{i18n.t('onboarding.hint_members')}</p>
          <div className="flex justify-between mt-4">
            <button
              className="px-4 py-2 rounded text-btn-text-primary bg-btn-success"
              onClick={() => {
                if (canProceedFamily) {
                  setShowErrors(false);
                  syncChildRooms();
                  setStep(2);
                } else {
                  setShowErrors(true);
                }
              }}
            >{i18n.t('onboarding.next')}</button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <div>
            <h2 className="text-xl font-semibold mb-1">{i18n.t('onboarding.step_rooms_title')}</h2>
            <p className="text-sm text-gray-700 whitespace-pre-line">{i18n.t('onboarding.step_rooms_help')}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-2">
            {options?.rooms?.length
              ? options.rooms.filter(r => !r.is_child_room).map(r => (
                <button key={r.room_key} className="px-3 py-2 rounded border hover:bg-gray-50" onClick={() => addCatalogRoom(r)}>{r.name}</button>
              ))
              : ROOM_TYPES.map(rt => {
                const localized = i18n.t(`rooms.${rt.type_key}`) || rt.display_name;
                return (
                  <button key={rt.type_key} className="px-3 py-2 rounded border hover:bg-gray-50" onClick={() => addRoomByType(rt.type_key, localized)}>{localized}</button>
                );
              })}
          </div>
          <div className="flex gap-2 items-center">
            <input value={custom_room_input} onChange={e => setCustomRoomInput(e.target.value)} placeholder={i18n.t('onboarding.custom_room_placeholder')} className="border rounded px-3 py-2 flex-1" />
            <button className="px-3 py-2 rounded text-btn-text-secondary bg-btn-secondary" onClick={addCustomRoom}>{i18n.t('onboarding.add_custom')}</button>
          </div>
          <div className="space-y-2">
            {room_drafts.map((r, idx) => (
              <div key={idx} className="flex items-center gap-2 border rounded p-2">
                <input value={r.name} onChange={e => setRoomDrafts(prev => prev.map((pr, i) => i===idx ? { ...pr, name: e.target.value, auto: false } : pr))} className="border rounded px-3 py-2 flex-1" />
                <span className="text-xs text-gray-500">{r.original_name}</span>
                <button className="px-2 py-1 rounded text-btn-text-cancel bg-btn-cancel" onClick={() => removeRoomAt(idx)}>{i18n.t('onboarding.remove')}</button>
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-4">
            <button className="px-4 py-2 rounded text-btn-text-cancel bg-btn-cancel" onClick={() => setStep(1)}>{i18n.t('onboarding.back')}</button>
            <button className="px-4 py-2 rounded text-btn-text-primary bg-btn-success" disabled={!canProceedRooms} onClick={() => { void loadOptions(); setStep(3); }}>{i18n.t('onboarding.next')}</button>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-300">{i18n.t('onboarding.hint_rooms')}</p>
        </div>
      )}

      {step === 3 && renderStarterTasks()}
      {step === 4 && renderStarterRewards()}

      {step === 5 && (
        <div className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold mb-2">{i18n.t('onboarding.step_review_title')}</h2>
            <div className="mb-3"><span className="font-medium">{i18n.t('onboarding.review_family')}:</span> {family_name}</div>
            <div className="mb-3">
              <div className="font-medium mb-1">{i18n.t('onboarding.review_members')}</div>
              <ul className="list-disc list-inside">
                {members.map((m, idx) => (
                  <li key={idx}>{m.name} – @{m.username} – {m.is_admin ? i18n.t('onboarding.role_admin') : i18n.t('onboarding.role_member')} – PIN ••••</li>
                ))}
              </ul>
            </div>
            <div className="mb-3">
              <div className="font-medium mb-1">{i18n.t('onboarding.review_rooms')}</div>
              <ul className="list-disc list-inside">
                {room_drafts.map((r, idx) => (
                  <li key={idx}>{r.name} <span className="text-xs text-gray-500">({r.original_name})</span></li>
                ))}
              </ul>
            </div>
            <div className="mb-3">
              <span className="font-medium">{i18n.t('onboarding.review_starters')}:</span>{' '}
              {i18n.t(pluralKey('onboarding.review_tasks', pickedTasks.length)).replace('{{n}}', String(pickedTasks.length))},{' '}
              {i18n.t(pluralKey('onboarding.review_rewards', pickedRewards.length)).replace('{{n}}', String(pickedRewards.length))}
            </div>
            <label className="flex items-start gap-3 mt-4 p-3 rounded border border-gray-200 dark:border-gray-600 cursor-pointer">
              <input type="checkbox" className="mt-1 w-5 h-5 shrink-0" checked={adultsNeedApproval} onChange={(e) => setAdultsNeedApproval(e.target.checked)} />
              <span>
                <span className="font-medium block">{i18n.t('approval_setting.label')}</span>
                <span className="text-sm text-gray-600 dark:text-gray-300">{i18n.t('approval_setting.setup_help')}</span>
              </span>
            </label>
          </div>
          {error && <div className="text-red-600 text-sm">{i18n.t('onboarding.error_generic')}</div>}
          <div className="flex justify-between mt-4">
            <button className="px-4 py-2 rounded text-btn-text-cancel bg-btn-cancel" onClick={() => setStep(4)}>{i18n.t('onboarding.back')}</button>
            <button className="px-4 py-2 rounded text-btn-text-primary bg-btn-success" onClick={onSave} disabled={saving}>{saving ? i18n.t('onboarding.processing') : i18n.t('onboarding.create_family')}</button>
          </div>
        </div>
      )}

      {step === 6 && (
        // The family is ready: point straight at the first task — the moment the app shows its value.
        <div className="space-y-4 rounded-lg border p-5">
          <h2 className="text-2xl font-semibold">🎉 {i18n.t('onboarding.done_title')}</h2>
          {result && (result.tasks > 0 || result.rewards > 0) && (
            <p>{i18n.t('onboarding.done_made').replace('{{tasks}}', String(result.tasks)).replace('{{rewards}}', String(result.rewards))}</p>
          )}
          <p className="font-semibold">{i18n.t('onboarding.done_try').replace('{{switch}}', i18n.t('header.switch_user')).replace('{{start}}', i18n.t('task_flow.start_task'))}</p>
          <p className="text-sm text-gray-700 dark:text-gray-300">{i18n.t('onboarding.done_admin').replace('{{approve}}', i18n.t('approve.title'))}</p>
          <div className="flex flex-wrap gap-2">
            <button className="px-4 py-2 rounded text-btn-text-primary bg-btn-success" onClick={() => navigate('/', { replace: true })}>{i18n.t('onboarding.go_home')}</button>
            <button className="px-4 py-2 rounded border" onClick={() => navigate('/admin', { replace: true })}>{i18n.t('onboarding.go_admin')}</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnboardingWizard;


